import { AbstractControl, FormArray, ValidationErrors, ValidatorFn } from '@angular/forms';

export function isFiniteNumericValue(value: any): boolean {
  return (typeof value === 'number' || typeof value === 'string' && value.trim() !== '') &&
    Number.isFinite(Number(value));
}

export function requiredTextValidator(control: AbstractControl): ValidationErrors | null {
  return typeof control.value === 'string' && control.value.trim() ? null : { required: true };
}

function emptyNumericValue(value: any): boolean {
  return value == null || typeof value === 'string' && value.trim() === '';
}

export function requiredSelectionValidator(control: AbstractControl): ValidationErrors | null {
  return emptyNumericValue(control.value) ? { required: true } : null;
}

export function finiteNumberValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (emptyNumericValue(control.value)) { return { required: true }; }
    return isFiniteNumericValue(control.value) ? null : { finiteNumber: true };
  };
}

export function nonNegativeFiniteNumberValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = Number(control.value);
    if (emptyNumericValue(control.value)) { return { required: true }; }
    if (!isFiniteNumericValue(control.value)) { return { finiteNumber: true }; }
    return Number.isFinite(value) && value >= 0 ? null : { nonNegative: true };
  };
}

export function positiveFiniteNumberValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = Number(control.value);
    if (emptyNumericValue(control.value)) { return { required: true }; }
    if (!isFiniteNumericValue(control.value)) { return { finiteNumber: true }; }
    return Number.isFinite(value) && value > 0 ? null : { positive: true };
  };
}

export function thermometerArraysValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const measurements = control.get('Measurements') as FormArray;
    const nModels = control.get('NModels') as FormArray;
    if (!measurements || !nModels) { return null; }
    const errors: ValidationErrors = {};
    if (measurements.length < 2) { errors.requiredMeasurements = true; }
    const nominal = measurements.controls.map(row => row.get('NominalTemperature').value);
    if (nominal.length >= 2 && nominal.every(isFiniteNumericValue) && new Set(nominal.map(Number)).size < 2) {
      errors.distinctNominalTemperature = true;
    }
    const measurementIds = measurements.controls.map(x => String(x.get('MeasurementId').value || '').trim());
    const nModelIds = nModels.controls.map(x => String(x.get('MeasurementId').value || '').trim());
    const duplicates = (ids: string[]) => ids.some((id, index) => !id || ids.indexOf(id) !== index);
    if (duplicates(measurementIds) || duplicates(nModelIds)) { errors.duplicateMeasurementId = true; }
    if (measurements.length !== nModels.length || nModelIds.some(id => measurementIds.indexOf(id) < 0) ||
        measurementIds.some(id => nModelIds.indexOf(id) < 0)) { errors.unmatchedMeasurementId = true; }
    return Object.keys(errors).length ? errors : null;
  };
}

export function acceptanceLimitsValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const lower = control.get('LowerLimit')?.value;
    const upper = control.get('UpperLimit')?.value;
    if (!isFiniteNumericValue(lower) || !isFiniteNumericValue(upper)) { return null; }
    return Number(lower) < Number(upper) ? null : { acceptanceLimits: true };
  };
}

export function orderedRangeValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.get('From') || !control.get('To') ||
        !isFiniteNumericValue(control.get('From').value) || !isFiniteNumericValue(control.get('To').value)) { return null; }
    const from = Number(control.get('From') && control.get('From').value);
    const to = Number(control.get('To') && control.get('To').value);
    return Number.isFinite(from) && Number.isFinite(to) && from <= to ? null : { orderedRange: true };
  };
}

